/**
 * 主题原生 modal 与挂件 UI 的寄居关系。
 *
 * <dialog>.showModal() 会把弹窗抬进 top layer，并按 HTML 的 inert 规则
 * 让它 flat tree 之外的一切不可交互——z-index 和 top-layer 绘制是两回事，
 * 光浮在 modal 之上按钮仍点不到（见 lessons-learned 2026-09-13）。挂件 UI
 * 要在 modal 开着时保持可见可点，只能把内容搬成 topmost modal 的 flat-tree
 * 后代：部件自有的寄居宿主（display:contents + open shadow）挂进 modal，
 * 内容停进它的 shadow，再按需用 manual popover 抬到 modal 之上。modal
 * 关了就搬回原来的 shadow，宿主撤走，主题节点一概不动。
 */

/** 活着的临时宿主 shadow：命中测试与焦点证据落到自家 UI 上都不算数。 */
const hostShadows = new Set<ShadowRoot>();

/**
 * node 是否落在任一挂件寄居 UI 里：寄居宿主元素本身，或其 shadow 内部。
 * 拾取模式用它把挂件自己的 UI（含寄居到 modal 里的部分）排除出可选目标。
 */
export function isInModalHost(node: Node): boolean {
  if (node instanceof Element) {
    const shadow = node.shadowRoot;
    if (shadow && hostShadows.has(shadow)) return true;
  }
  const root = node.getRootNode();
  return root instanceof ShadowRoot && hostShadows.has(root);
}

export interface ModalParkingOptions {
  /**
   * 寄居宿主标签名：部件自有的自定义标签——主题按标签/类名写的选择器
   * （dialog > div 这类）匹配不到它；通配符选择器（dialog > *）仍会
   * 命中，隔离不靠标签名本身。停进它 open shadow 里的后代才是真正
   * 的样式隔离边界：主题 CSS 一概穿不进来。
   */
  hostTag: string;
  /** 没有 modal 时内容停回的 shadow。 */
  home: ShadowRoot;
  /** 在 home 与寄居 shadow 之间搬动的内容元素。 */
  content: HTMLElement;
  /**
   * manual popover 提升策略：
   * "always" —— 停在 home 也保持 top layer（覆盖层本来就压在页面之上）；
   * "parked" —— 只在寄居 modal 期间提升，回 home 还原成普通元素。
   */
  raise?: "always" | "parked";
}

export interface ModalParking {
  /** 切换非 modal 时的宿主；modal 开着时仍优先寄居 modal。 */
  setHome(home: ShadowRoot): void;
  dispose(): void;
}

/** 命中测试的采样点离 dialog 边框的内缩量。 */
const HIT_TEST_INSET = 4;

/**
 * 让 content 始终待在能用的地方：没有 modal 就停回 home；有 modal 就
 * 寄居进 topmost modal 的 flat tree（不然 inert 着根本点不到），并按
 * 策略用 manual popover 抬进 top layer。
 *
 * topmost 维护：open 变化的 dialog 视为新开压栈，栈里不连接的、不再
 * modal 的清掉；因意外退出（嵌套 showModal 把外层 inert 掉、主题 moveBefore
 * 整体搬走、栈顶被 skip）导致记序失真时，用页面证据实测重排：实底焦点
 * 最近的 modal 祖先优先，焦点落空退到候选边框内的命中测试——inert 子树
 * 两边都进不来，证据来自哪个 modal，它就是 topmost。
 */
export function createModalParking(options: ModalParkingOptions): ModalParking {
  const { hostTag, content } = options;
  let home = options.home;
  const raise = options.raise ?? "parked";
  let host: HTMLElement | undefined;
  let hostShadow: ShadowRoot | undefined;
  const modalStack: HTMLDialogElement[] = [];
  /** 栈序被证据核实过的标记：exit 观察不清 exit 后谁最顶时置 false。 */
  let modalTopKnown = true;
  let raised = false;
  /** show 抛过错已永久降级：摘掉属性回流内渲染，不再重试。 */
  let popoverBroken = false;
  let disposed = false;

  /** open 的不一定是 modal：show() 也置 open，只有 :modal 才让页面 inert。 */
  function isModalDialog(el: Element): el is HTMLDialogElement {
    if (!(el instanceof HTMLDialogElement)) return false;
    try {
      return el.matches(":modal");
    } catch {
      // 老浏览器或不支持 :modal 的环境：open 了当 modal 处理。
      return el.hasAttribute("open");
    }
  }

  /**
   * el 在候选集里的最近 modal 祖先：沿 flat tree 上爬（穿 shadow），
   * 第一个 modal 就是——嵌套 modal 里外层候选也"包含"内层焦点，
   * 按 DOM 序 find 会错选外层那个 inert 的。
   */
  function modalAncestorOf(
    el: Element | null,
    candidates: Set<HTMLDialogElement>,
  ): HTMLDialogElement | undefined {
    let node: Node | null = el;
    while (node) {
      // 自家寄居 UI 不是证据：落在寄居 shadow 里只能说明现在挂在哪，
      // 拿它推 topmost 会把陈旧位置自我坐实。
      if (node instanceof ShadowRoot && hostShadows.has(node)) return undefined;
      if (node instanceof HTMLDialogElement && candidates.has(node))
        return node;
      node = node instanceof ShadowRoot ? node.host : node.parentNode;
    }
    return undefined;
  }

  /** 实底焦点：document.activeElement 停在 shadow 宿主，穿下去拿真正聚焦的元素。 */
  function deepActiveElement(): Element | null {
    let el: Element | null = document.activeElement;
    while (el instanceof HTMLElement && el.shadowRoot?.activeElement) {
      el = el.shadowRoot.activeElement;
    }
    return el;
  }

  /**
   * 已开着的 modal 里找 topmost：只有它非 inert（modal 一开，同层之前
   * 的 modal 也被 inert 掉）。全只读，不动焦点不发事件：
   * 1. 焦点被 inert 锁在 topmost 的 flat tree 里——实底焦点最近的
   *    modal 祖先就是它（showModal 本身会做焦点处理）；
   * 2. 焦点落空退到命中测试：inert 子树被 hit test 跳过，候选边框内
   *    的采样点命中的元素的最近 modal 祖先才非 inert；
   * 3. 都没有返回 undefined，调用方按既有顺序兜底并留注释。
   */
  function findTopModal(
    candidates: HTMLDialogElement[],
  ): HTMLDialogElement | undefined {
    const set = new Set(candidates);
    const byFocus = modalAncestorOf(deepActiveElement(), set);
    if (byFocus) return byFocus;
    if (typeof document.elementFromPoint !== "function") return undefined;
    try {
      for (const d of candidates) {
        const r = d.getBoundingClientRect();
        const points: [number, number][] = [
          [r.left + HIT_TEST_INSET, r.top + HIT_TEST_INSET],
          [r.right - HIT_TEST_INSET, r.top + HIT_TEST_INSET],
          [r.left + r.width / 2, r.top + r.height / 2],
          [r.left + HIT_TEST_INSET, r.bottom - HIT_TEST_INSET],
          [r.right - HIT_TEST_INSET, r.bottom - HIT_TEST_INSET],
        ];
        for (const [x, y] of points) {
          if (x < 0 || y < 0 || x > window.innerWidth || y > window.innerHeight)
            continue;
          const owner = modalAncestorOf(document.elementFromPoint(x, y), set);
          if (owner) return owner;
        }
      }
    } catch {
      /* elementFromPoint 在隐藏文档等状态下不可靠：错过不追。 */
    }
    return undefined;
  }

  /** 已经开着的 modal 播种入栈：topmost 实测，其余按 DOM 序垫底。 */
  function seedModalStack() {
    const preModals = [...document.querySelectorAll("dialog")].filter(
      isModalDialog,
    );
    if (preModals.length === 0) return;
    const top = findTopModal(preModals) ?? preModals.at(-1);
    for (const d of preModals) if (d !== top) modalStack.push(d);
    if (top) modalStack.push(top);
    modalTopKnown = true;
  }

  /** 收掉顶层展示：hide 抛错只说明已被隐式收起，记账保持一致即可。 */
  function unraise() {
    if (!raised) return;
    try {
      content.hidePopover();
    } catch {
      /* 宿主离文档等情形会隐式收起，hide 抛错只说明已经不在顶层。 */
    }
    raised = false;
  }

  /** 摘掉提升时写上的中性样式，把外壳还原成内容自己的元素。 */
  function clearRaiseStyles() {
    content.style.background = "";
    content.style.border = "";
    content.style.margin = "";
    content.style.padding = "";
  }

  /**
   * 抬进 top layer。不支持 popover 的环境退回流内渲染；show 抛错
   * 永久降级（摘掉属性，不然被 UA 的 display:none 藏掉）。
   */
  function raiseIt() {
    if (popoverBroken || typeof content.showPopover !== "function") return;
    if (!content.hasAttribute("popover")) {
      content.setAttribute("popover", "manual");
      // popover 的 UA 样式自带 border/padding/背景，这层外壳不能要。
      content.style.background = "transparent";
      content.style.border = "none";
      content.style.margin = "0";
      content.style.padding = "0";
    }
    unraise();
    try {
      content.showPopover();
      raised = true;
    } catch {
      raised = false;
      popoverBroken = true;
      content.removeAttribute("popover");
    }
  }

  /**
   * 搬家：先收 popover 再移动（祖先搬家会隐式收起 showing 状态，自己先
   * 收保持记账一致），落位后按策略重抬。停在 home 且不升顶层时把
   * popover 属性摘干净——带着属性又不 show 会被 UA 样式藏起来。
   */
  function park(parent: ShadowRoot) {
    const wantRaise = raise === "always" || parent !== home;
    if (content.parentNode !== parent) {
      unraise();
      parent.append(content);
    }
    if (wantRaise) {
      // 寄居在 modal 里时每次安置都重抬：宿主换顶或被主题整体搬动会
      // 隐式收起 popover。停在 home 只有掉出顶层才补抬。
      if (parent !== home || !raised) raiseIt();
    } else {
      // 回家就不升顶层：属性和提升样式都摘掉，还原成普通元素。
      content.removeAttribute("popover");
      clearRaiseStyles();
    }
  }

  /** 按当前 modal 栈安置内容：有 modal 寄居进它的 flat tree，没有就回家。 */
  function sync() {
    const prevTop = modalStack.at(-1);
    for (let i = modalStack.length - 1; i >= 0; i--) {
      const d = modalStack[i];
      if (!d || !d.isConnected || !isModalDialog(d)) modalStack.splice(i, 1);
    }
    let top = modalStack.at(-1);
    if (top !== prevTop) modalTopKnown = false;
    if (top && !modalTopKnown) {
      const verified = findTopModal(modalStack);
      if (verified && verified !== top) {
        modalStack.splice(modalStack.indexOf(verified), 1);
        modalStack.push(verified);
        top = verified;
      }
      modalTopKnown = true;
    }
    if (!top) {
      park(home);
      if (host) {
        if (hostShadow) hostShadows.delete(hostShadow);
        host.remove();
        host = undefined;
        hostShadow = undefined;
      }
      return;
    }
    if (!host) {
      host = document.createElement(hostTag);
      // 宿主对主题不可见：display:contents 让它不产生盒子，open shadow
      // 把停进来的内容包在挂件自己的树里。
      host.style.display = "contents";
      hostShadow = host.attachShadow({ mode: "open" });
      hostShadows.add(hostShadow);
    }
    if (host.parentElement !== top) top.append(host);
    if (hostShadow) park(hostShadow);
  }

  // modal 的 open 变化（开或关）都可能改变谁是 topmost；childList 盯住
  // 主题把开着的 modal 连节点摘掉或把栈内 modal 搬走的情形。自家寄居
  // 宿主的进出不是 dialog 也不含栈内 modal，天然被跳过。
  // 边界：观察穿不进主题自定义元素的 shadow root，里面开的 modal 认不出。
  const dialogWatch = new MutationObserver((records) => {
    if (disposed) return;
    let dirty = false;
    for (const record of records) {
      if (record.type === "childList") {
        const touched = (n: Node) =>
          n instanceof HTMLDialogElement ||
          modalStack.some((d) => n.contains(d));
        if (
          modalStack.some((d) => !d.isConnected) ||
          [...record.addedNodes, ...record.removedNodes].some(touched)
        )
          dirty = true;
        continue;
      }
      const el = record.target;
      if (!(el instanceof HTMLDialogElement)) continue;
      if (isModalDialog(el)) {
        const i = modalStack.indexOf(el);
        if (i >= 0) modalStack.splice(i, 1);
        modalStack.push(el);
        modalTopKnown = true;
      }
      dirty = true;
    }
    if (dirty) sync();
  });
  dialogWatch.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["open"],
    childList: true,
    subtree: true,
  });
  // 种子要在内容进 top layer 之前算：命中测试不能打到自己。
  seedModalStack();
  sync();

  return {
    setHome(next) {
      if (disposed || next === home) return;
      if (home !== options.home) hostShadows.delete(home);
      home = next;
      if (home !== options.home) hostShadows.add(home);
      sync();
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      if (home !== options.home) hostShadows.delete(home);
      dialogWatch.disconnect();
      modalStack.length = 0;
      unraise();
      if (content.parentNode !== home) home.append(content);
      content.removeAttribute("popover");
      clearRaiseStyles();
      if (host) {
        if (hostShadow) hostShadows.delete(hostShadow);
        host.remove();
        host = undefined;
        hostShadow = undefined;
      }
    },
  };
}
